import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    CollegeFinderResultItem,
    CollegeFinderSearchHistory,
    CollegeFinderSearchItem,
    fetchUserCollegeSearches,
} from "../services/polarisService";
import "../styles/polaris-admin.css";

const DIVISION_LABELS: Record<string, string> = {
    NCAA_D1: "NCAA Division I",
    NCAA_D2: "NCAA Division II",
    NCAA_D3: "NCAA Division III",
    NAIA: "NAIA",
    NJCAA: "NJCAA",
};

const SETTING_LABELS: Record<string, string> = {
    CITY: "City",
    SUBURB: "Suburb",
    TOWN: "Town",
    RURAL: "Rural",
};

const TYPE_LABELS: Record<string, string> = {
    PUBLIC: "Public",
    PRIVATE_NP: "Private",
    PRIVATE_FP: "Private for-profit",
};

const SEARCHES_PER_PAGE = 10;

function getPageNumbers(current: number, total: number): (number | "ellipsis")[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    const pages: (number | "ellipsis")[] = [];
    pages.push(0);
    if (current > 3) pages.push("ellipsis");
    for (let i = Math.max(1, current - 2); i <= Math.min(total - 2, current + 2); i++) {
        pages.push(i);
    }
    if (current < total - 4) pages.push("ellipsis");
    pages.push(total - 1);
    return pages;
}

function label(value: string, labels: Record<string, string>): string {
    return labels[value] ?? value;
}

function formatWhen(value: string | undefined): string {
    if (!value) {
        return "Search";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return "Search";
    }
    return new Intl.DateTimeFormat("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
    }).format(parsed);
}

function sizeLabel(search: CollegeFinderSearchItem): string | null {
    const min = search.minSize;
    const max = search.maxSize;
    if (min == null && max == null) {
        return null;
    }
    const format = (value: number) => value.toLocaleString();
    if (min != null && max != null) {
        return `${format(min)}–${format(max)} students`;
    }
    if (min != null) {
        return `At least ${format(min)} students`;
    }
    return `Up to ${format(max as number)} students`;
}

function criteriaChips(search: CollegeFinderSearchItem): string[] {
    const chips = [
        ...search.sports,
        ...search.divisions.map((division) => label(division, DIVISION_LABELS)),
        ...search.states,
    ];
    const size = sizeLabel(search);
    if (size) {
        chips.push(size);
    }
    chips.push(...search.campusSettings.map((setting) => label(setting, SETTING_LABELS)));
    if (search.schoolType) {
        chips.push(label(search.schoolType, TYPE_LABELS));
    }
    return chips;
}

function friendlyLoadError(error: unknown): string {
    const message = error instanceof Error ? error.message : "";
    if (message === "That user could not be found." || message === "Could not load college searches.") {
        return message;
    }
    return "Could not load college searches.";
}

function resultDetail(result: CollegeFinderResultItem): string {
    const place = [result.city, result.state].filter(Boolean).join(", ");
    const extra = [
        place,
        result.division ? label(result.division, DIVISION_LABELS) : null,
        result.size != null ? `${result.size.toLocaleString()} students` : null,
        result.campusSetting ? label(result.campusSetting, SETTING_LABELS) : null,
    ].filter(Boolean);
    return extra.join(" · ");
}

export default function PolarisUserCollegeSearchesPage() {
    const { userId } = useParams();
    const id = Number(userId);
    const [page, setPage] = useState(0);
    const [chatPage, setChatPage] = useState(0);
    const [history, setHistory] = useState<CollegeFinderSearchHistory | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!Number.isFinite(id)) {
            setLoading(false);
            setError("That user could not be found.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setHistory(await fetchUserCollegeSearches(id, page, chatPage));
        } catch (e: unknown) {
            setHistory(null);
            setError(friendlyLoadError(e));
        } finally {
            setLoading(false);
        }
    }, [id, page, chatPage]);

    useEffect(() => {
        void load();
    }, [load]);

    const title = history ? `${history.displayName} · College searches` : "College searches";
    const wizardSearches = history?.searches ?? [];
    const chatSearches = history?.chatSearches ?? [];
    const wizardTotal = history?.totalSearches ?? 0;
    const chatTotal = history?.chatTotalSearches ?? 0;
    const hasAnySearch = wizardTotal > 0 || chatTotal > 0;

    return (
        <section className="pa-section" data-testid="polaris-user-college-searches-page">
            <Link to="/polaris-admin/users" className="pa-back-link">
                Back to users
            </Link>
            <h2 className="pa-section-title">{title}</h2>
            {history && <p className="pa-muted">{history.email}</p>}
            {error && (
                <div className="pa-error" role="alert">
                    {error}
                </div>
            )}
            {loading && <p className="pa-college-search-loading">Loading college searches…</p>}
            {!loading && history && !hasAnySearch && (
                <p className="pa-college-search-empty">This user has not run a college search yet.</p>
            )}
            {history && hasAnySearch && (
                <>
                    <SearchSection
                        heading="Guided searches"
                        emptyLabel="This user has not run a guided college search yet."
                        searches={wizardSearches}
                    />
                    {wizardTotal > 0 && (
                        <SearchPagination
                            currentPage={page}
                            totalPages={Math.max(history.totalPages, 1)}
                            totalSearches={wizardTotal}
                            onPageChange={setPage}
                            nextLabel="Next page"
                            previousLabel="Previous page"
                            pageLabelPrefix="Page"
                        />
                    )}
                    <SearchSection
                        heading="AI chat searches"
                        emptyLabel="This user has not run an AI chat college search yet."
                        searches={chatSearches}
                        showMessage
                    />
                    {chatTotal > 0 && (
                        <SearchPagination
                            currentPage={chatPage}
                            totalPages={Math.max(history.chatTotalPages, 1)}
                            totalSearches={chatTotal}
                            onPageChange={setChatPage}
                            nextLabel="Next chat page"
                            previousLabel="Previous chat page"
                            pageLabelPrefix="Chat page"
                        />
                    )}
                </>
            )}
        </section>
    );
}

function SearchSection({
    heading,
    emptyLabel,
    searches,
    showMessage = false,
}: {
    heading: string;
    emptyLabel: string;
    searches: CollegeFinderSearchItem[];
    showMessage?: boolean;
}) {
    return (
        <section className="pa-college-search-section">
            <h3 className="pa-college-search-section-title">{heading}</h3>
            {searches.length === 0 ? (
                <p className="pa-college-search-empty">{emptyLabel}</p>
            ) : (
                <div className="pa-college-search-list">
                    {searches.map((search) => (
                        <article
                            key={search.id}
                            className="pa-college-search-card"
                            data-testid={`college-search-${search.id}`}
                        >
                            <p className="pa-college-search-when">{formatWhen(search.searchedAt)}</p>
                            {showMessage && search.userMessage ? (
                                <p className="pa-college-search-message">{search.userMessage}</p>
                            ) : null}
                            <div className="pa-college-search-criteria">
                                {criteriaChips(search).map((chip) => (
                                    <span key={chip} className="pa-college-search-chip">
                                        {chip}
                                    </span>
                                ))}
                            </div>
                            {search.results.length === 0 ? (
                                <p className="pa-college-search-empty">No matching colleges were returned.</p>
                            ) : (
                                <ul className="pa-college-search-results">
                                    {search.results.map((result) => (
                                        <li
                                            key={`${search.id}-${result.ipedsId ?? result.name}`}
                                            className="pa-college-search-result"
                                        >
                                            <strong>{result.name || "College"}</strong>
                                            <span>{resultDetail(result)}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </article>
                    ))}
                </div>
            )}
        </section>
    );
}

function SearchPagination({
    currentPage,
    totalPages,
    totalSearches,
    onPageChange,
    nextLabel,
    previousLabel,
    pageLabelPrefix,
}: {
    currentPage: number;
    totalPages: number;
    totalSearches: number;
    onPageChange: (page: number) => void;
    nextLabel: string;
    previousLabel: string;
    pageLabelPrefix: string;
}) {
    const start = currentPage * SEARCHES_PER_PAGE + 1;
    const end = Math.min(currentPage * SEARCHES_PER_PAGE + SEARCHES_PER_PAGE, totalSearches);
    const pages = getPageNumbers(currentPage, totalPages);

    return (
        <div className="pa-pagination pa-college-search-pagination">
            <div className="pa-pagination-info">
                Showing <strong>{start}–{end}</strong> of <strong>{totalSearches}</strong> searches
            </div>
            <div className="pa-pagination-controls">
                <button
                    type="button"
                    className="pa-page-btn"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                    aria-label={previousLabel}
                >
                    Previous
                </button>
                {pages.map((entry, index) =>
                    entry === "ellipsis" ? (
                        <span key={`ellipsis-${index}`} className="pa-page-ellipsis">…</span>
                    ) : (
                        <button
                            key={entry}
                            type="button"
                            className={`pa-page-btn${entry === currentPage ? " active" : ""}`}
                            onClick={() => onPageChange(entry)}
                            aria-label={`${pageLabelPrefix} ${entry + 1}`}
                            aria-current={entry === currentPage ? "page" : undefined}
                        >
                            {entry + 1}
                        </button>
                    )
                )}
                <button
                    type="button"
                    className="pa-page-btn"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                    aria-label={nextLabel}
                >
                    Next
                </button>
            </div>
        </div>
    );
}
